import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { setIsChangeValues, setIsPEPSignature } from 'store/anketa';
import { getDocumentsByHash, sendSmsCode } from 'data-providers/mainAnketaProvider';
import { AGREEMENT_DOCUMENTS_TYPES, SMS_VERIFICATION_PROVIDER } from './constants';
import { onSignPEPClick } from 'metrika/mainAnketa';

import { Link } from 'components/uikit/Link/Link';

import * as SMSVerificationStore from 'store/confirmSMS';
import * as help from './helpers';

export const useSignPEPPresenter = ({ anketaData, anketaErrors, onSave }) => {
  const rootDispatch = useDispatch();
  const { addToast, removeAllToasts } = useToasts();

  const { id: userId } = useSelector((store) => store.user);
  const { isPEPSignature, pdf, otpDocLink, isChangeValues } = useSelector(
    (state) => state.anketa || {}
  );
  const { timerStartTime, fetchError } = useSelector((store) => store.confirmSMS);
  const {
    timerStartTime: teleponyTimerStartTime,
    isConfirmed: isTeleponyConfirmed,
    isFetching: isTeleponyRequestFetching,
  } = useSelector((store) => store.confirmSMS.teleponyConfirm);

  const [data, setData] = useState({ agreementPEP: true, agreementProc: true, agreementBKI: true });
  const [isDisabled, setIsDisabled] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [mainVerification, setMainVerification] = useState(false);
  const [secondaryVerification, setSecondaryVerification] = useState(false);
  const [firstOtpRequest, setFirstOtpRequest] = useState(false);
  const [savedSMSProvider, setSavedSMSProvider] = useState(null);
  const [availableTelepony, setAvailableTelepony] = useState(false);

  const hasAnketaErrors = useMemo(() => help.hasAnketaErrors(anketaErrors), [anketaErrors]);
  const customer = useMemo(() => help.getCustomer(anketaData), [anketaData]);

  const documentsHandler = async (event, type) => {
    event.stopPropagation();
    event.preventDefault();

    if (!anketaData?.application?.id || !AGREEMENT_DOCUMENTS_TYPES[type] || !customer) {
      return;
    }

    addToast('Загрузка документа', {
      appearance: 'success',
      autoDismiss: true,
    });

    await getDocumentsByHash(otpDocLink[AGREEMENT_DOCUMENTS_TYPES[type].type]);
  };

  const getDocumentsLabel = (type) => {
    if (!isPEPSignature || !otpDocLink[AGREEMENT_DOCUMENTS_TYPES[type].type]) {
      return AGREEMENT_DOCUMENTS_TYPES[type]?.name || '';
    }

    return (
      <Link href="#" onClick={(e) => documentsHandler(e, type)} data-test={`link ${type}`}>
        {AGREEMENT_DOCUMENTS_TYPES[type]?.name || ''}
      </Link>
    );
  };

  const getNotificationText = useMemo(() => {
    if (isChangeValues) {
      return 'Данные клиента были изменены. Отправьте клиенту СМС со ссылкой на документы и кодом для их подписания';
    }
    return 'Отправьте клиенту СМС со ссылкой на документы и кодом для их подписания';
  }, [isChangeValues]);

  const changeHandler = (field) => {
    setData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const otpRequest = async () => {
    const otpResult = await sendSmsCode({
      appId: anketaData?.application?.id,
      dealerId: anketaData?.application?.dealer?.id,
      data: help.getSmsParams(customer),
      userId,
    });

    rootDispatch(SMSVerificationStore.setOtpId(otpResult?.id || null));

    return otpResult;
  };

  const signHandler = async () => {
    onSignPEPClick();
    rootDispatch(SMSVerificationStore.setFetchError(false));

    if (savedSMSProvider) {
      if (savedSMSProvider === SMS_VERIFICATION_PROVIDER.TELEPONY && availableTelepony) {
        setMainVerification(true);
      } else {
        setSecondaryVerification(true);
      }
      return;
    }

    if (isTeleponyRequestFetching || firstOtpRequest) {
      return;
    }

    let SMSprovider = savedSMSProvider;
    let isTeleponyAvailable = availableTelepony;

    if (
      anketaData?.application?.id &&
      anketaData?.application?.dealer?.id &&
      help.checkCustomer(customer)
    ) {
      setFirstOtpRequest(true);
      rootDispatch(SMSVerificationStore.setTeleponyRequestFetching(true));

      const otpResult = await otpRequest();

      if (!otpResult) {
        rootDispatch(SMSVerificationStore.setFetchError(true));
        setFirstOtpRequest(false);
        return;
      }

      SMSprovider = otpResult?.provider || SMS_VERIFICATION_PROVIDER.ECREDIT_SMS;

      isTeleponyAvailable = otpResult.current_count <= otpResult.total;

      setSavedSMSProvider(SMSprovider);
      setAvailableTelepony(isTeleponyAvailable);

      if (SMSprovider === SMS_VERIFICATION_PROVIDER.TELEPONY) {
        rootDispatch(SMSVerificationStore.setTeleponyTotalResendTries(otpResult.total));
        rootDispatch(SMSVerificationStore.setTeleponyCurrentResendTries(otpResult.current_count));
      }

      if (SMSprovider === SMS_VERIFICATION_PROVIDER.TELEPONY && isTeleponyAvailable) {
        setMainVerification(true);

        if (!teleponyTimerStartTime) {
          rootDispatch(SMSVerificationStore.setTeleponyTimerStart(Math.floor(Date.now() / 1000)));
        }
      } else {
        setSecondaryVerification(true);

        if (!timerStartTime) {
          rootDispatch(SMSVerificationStore.setTimerStart(Math.floor(Date.now() / 1000)));
        }
      }

      rootDispatch(SMSVerificationStore.setTeleponyRequestFetching(false));
    }
  };

  const resetVerificationState = () => {
    setMainVerification(false);
    setSecondaryVerification(false);
    setSavedSMSProvider(null);
    setFirstOtpRequest(false);
    setAvailableTelepony(false);

    rootDispatch(SMSVerificationStore.resetSMSVerification());
  };

  const verificationCloseHandler = () => {
    setMainVerification(false);
    setSecondaryVerification(false);
  };

  const verificationSuccessHandler = () => {
    onSave(true);
    rootDispatch(setIsPEPSignature(true));
    rootDispatch(setIsChangeValues(false));
    setIsSuccess(true);
    resetVerificationState();
  };

  const closeSuccessPopupHandler = () => {
    setIsSuccess(false);
    resetVerificationState();
  };

  const onMainSMSClickHandler = () => {
    setMainVerification(false);
    setSecondaryVerification(true);
    setSavedSMSProvider(SMS_VERIFICATION_PROVIDER.ECREDIT_SMS);

    rootDispatch(SMSVerificationStore.setTimerStart(Math.floor(Date.now() / 1000)));
  };

  useEffect(() => {
    setIsDisabled(
      !data.agreementPEP ||
        !data.agreementProc ||
        !data.agreementBKI ||
        hasAnketaErrors ||
        isPEPSignature
    );
  }, [hasAnketaErrors, data, isPEPSignature]);

  useEffect(() => {
    if (pdf) {
      removeAllToasts();
      help.saveDocument(pdf);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf]);

  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        closeSuccessPopupHandler();
      }, 2000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (fetchError) {
      setTimeout(() => {
        closeSuccessPopupHandler();
      }, 2000);
    }
  }, [fetchError]);

  useEffect(() => {
    if (firstOtpRequest) {
      resetVerificationState();
    }
  }, [customer]);

  useEffect(() => {
    if (isChangeValues) {
      resetVerificationState();
    }
  }, [isChangeValues]);

  useEffect(() => {
    if (isTeleponyConfirmed && !isTeleponyRequestFetching) {
      verificationSuccessHandler();
    }
  }, [isTeleponyConfirmed, isTeleponyRequestFetching]);

  return {
    data,
    customer,
    isChangeValues,
    isPEPSignature,
    isDisabled,
    isSuccess,
    mainVerification,
    secondaryVerification,
    fetchError,
    getNotificationText,
    isTeleponyRequestFetching,
    changeHandler,
    getDocumentsLabel,
    signHandler,
    onMainSMSClickHandler,
    verificationCloseHandler,
    verificationSuccessHandler,
    closeSuccessPopupHandler,
  };
};
